/**
 * RecentView — "what did I touch recently"
 *
 * A single flat list, sorted by `updatedAt` desc, grouped by recency buckets
 * (Today / Yesterday / This Week / Earlier). Inspired by Notion's
 * "Last edited" sort and macOS Finder's Recents.
 *
 * Mobile-friendly out of the box: the bucket headers stick, and rows are
 * tap-sized.
 */

import React, { useMemo } from 'react';
import {
  Paper,
  Text,
  Group,
  Stack,
  Badge,
  ActionIcon,
  Menu,
  Box,
  Divider,
} from '@mantine/core';
import {
  IconDots,
  IconEdit,
  IconTrash,
  IconCopy,
  IconClock,
  IconUser,
} from '@tabler/icons-react';
import type { DatabaseItem, Database } from '../../../types/models/Database';
import { PropertyChip } from '../../common/PropertyChip';
import { resolveCover, getGradientForId } from '../../database/coverResolver';

interface RecentViewProps {
  database: Database;
  items: DatabaseItem[];            // already sorted by updatedAt desc
  onItemSelect?: (item: DatabaseItem) => void;
  onItemUpdate?: (item: DatabaseItem) => void;
  onItemDelete?: (itemId: string) => void;
  onItemDuplicate?: (item: DatabaseItem) => void;
  onPropertyChange?: (itemId: string, propertyId: string, value: any) => void;
}

type Bucket = 'Today' | 'Yesterday' | 'This Week' | 'Earlier';

function bucketFor(date: Date | string | undefined, now = new Date()): Bucket {
  if (!date) return 'Earlier';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return 'Earlier';
  const startOfDay = new Date(now); startOfDay.setHours(0, 0, 0, 0);
  const startOfYesterday = new Date(startOfDay); startOfYesterday.setDate(startOfDay.getDate() - 1);
  const startOfWeek = new Date(startOfDay); startOfWeek.setDate(startOfDay.getDate() - startOfDay.getDay());

  if (d >= startOfDay) return 'Today';
  if (d >= startOfYesterday) return 'Yesterday';
  if (d >= startOfWeek) return 'This Week';
  return 'Earlier';
}

function relativeTime(date: Date | string | undefined, now = new Date()): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = now.getTime() - d.getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString();
}

export const RecentView: React.FC<RecentViewProps> = ({
  database,
  items,
  onItemSelect,
  onItemUpdate,
  onItemDelete,
  onItemDuplicate,
  onPropertyChange,
}) => {
  // Group by recency bucket
  const grouped = useMemo(() => {
    const order: Bucket[] = ['Today', 'Yesterday', 'This Week', 'Earlier'];
    const map = new Map<Bucket, DatabaseItem[]>();
    order.forEach(b => map.set(b, []));
    items.forEach(item => {
      const b = bucketFor(item.updatedAt);
      map.get(b)!.push(item);
    });
    return order.map(b => ({ bucket: b, items: map.get(b)! })).filter(g => g.items.length > 0);
  }, [items]);

  if (items.length === 0) {
    return (
      <Box p="xl" style={{ textAlign: 'center' }}>
        <IconClock size={48} stroke={1.2} style={{ opacity: 0.4, marginBottom: 12 }} />
        <Text c="dimmed">No items yet. Create one to see it here.</Text>
      </Box>
    );
  }

  return (
    <Box style={{ width: '100%', maxWidth: 800, margin: '0 auto', padding: '16px 20px 32px' }}>
      {grouped.map(({ bucket, items: bucketItems }) => (
        <Box key={bucket} mb="lg">
          <Group gap="xs" mb="xs" style={{ position: 'sticky', top: 0, paddingTop: 8, paddingBottom: 4, background: 'var(--mantine-color-body)', zIndex: 1 }}>
            <Text fw={700} size="sm" c="dimmed" style={{ letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              {bucket}
            </Text>
            <Badge size="xs" variant="light" color="gray">{bucketItems.length}</Badge>
          </Group>
          <Stack gap={6}>
            {bucketItems.map(item => (
              <RecentRow
                key={item.id}
                item={item}
                database={database}
                onItemSelect={onItemSelect}
                onItemUpdate={onItemUpdate}
                onItemDelete={onItemDelete}
                onItemDuplicate={onItemDuplicate}
                onPropertyChange={onPropertyChange}
              />
            ))}
          </Stack>
        </Box>
      ))}
    </Box>
  );
};

interface RowProps {
  item: DatabaseItem;
  database: Database;
  onItemSelect?: (i: DatabaseItem) => void;
  onItemUpdate?: (i: DatabaseItem) => void;
  onItemDelete?: (id: string) => void;
  onItemDuplicate?: (i: DatabaseItem) => void;
  onPropertyChange?: (id: string, propId: string, v: any) => void;
}

const RecentRow: React.FC<RowProps> = ({ item, database, onItemSelect, onItemUpdate, onItemDelete, onItemDuplicate, onPropertyChange }) => {
  // Cover: prefer item-level 'cover' property URL, then a schema
  // 'image' property, then a deterministic per-item gradient. The
  // shared resolver handles the fallback chain.
  const coverVal = resolveCover(item, database);
  const isRemote = coverVal && /^https?:\/\//.test(coverVal);
  const isData = coverVal && coverVal.startsWith('data:');
  const coverStyle: React.CSSProperties = coverVal
    ? (isRemote || isData
        ? {
            backgroundImage: `url(${coverVal})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }
        : { background: coverVal })
    : { background: getGradientForId(item.id) };

  return (
    <Paper
      p="sm"
      radius="md"
      withBorder
      className="fc-card"
      style={{
        cursor: 'pointer',
        transition: 'background 120ms ease',
      }}
      onClick={() => onItemSelect?.(item)}
    >
      <Group justify="space-between" wrap="nowrap" align="center">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          {/* Cover thumbnail — 40x40 rounded square. Falls back to first-letter
              avatar when no cover is set on the item. */}
          <div
            style={{
              width: 40, height: 40, borderRadius: 6,
              flexShrink: 0, ...coverStyle,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontWeight: 600, fontSize: 16,
              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
            }}
          >
            {coverVal ? null : (item.title[0]?.toUpperCase() ?? '?')}
          </div>
          <Box style={{ minWidth: 0, flex: 1 }}>
            <Text size="sm" fw={500} lineClamp={1}>{item.title}</Text>
            <Group gap="xs" wrap="nowrap" mt={2}>
              <Text size="xs" c="dimmed">{relativeTime(item.updatedAt)}</Text>
              {item.properties.slice(0, 2).map(p => {
                const def = database.schema.properties.find(d => d.id === p.propertyId);
                if (!def) return null;
                return (
                  <PropertyChip
                    key={p.propertyId}
                    property={def}
                    value={p.value}
                    /* F 490 — the owner of this date, so a reminder can
                       point at the record that holds it. */
                    ownerId={item.id}
                    ownerPropertyId={p.propertyId}
                    notificationText={item.title}
                    onChange={(v) => onPropertyChange?.(item.id, p.propertyId, v)}
                    /* MEASURED: `size="xs"` was never a PropertyChip prop —
                       the component has `compact?: boolean`. It was passed
                       here and silently ignored. Now it is the real prop, so
                       the chip in a dense recent-list row is actually
                       compact. */
                    compact
                  />
                );
              })}
            </Group>
          </Box>
        </Group>
        <Group gap={4} onClick={(e) => e.stopPropagation()}>
          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
            {relativeTime(item.updatedAt)}
          </Text>
          <Menu shadow="md" position="bottom-end">
            <Menu.Target>
              <ActionIcon size="sm" variant="subtle"><IconDots size={14} /></ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => onItemUpdate?.(item)}>Edit</Menu.Item>
              <Menu.Item leftSection={<IconCopy size={14} />} onClick={() => onItemDuplicate?.(item)}>Duplicate</Menu.Item>
              <Menu.Divider />
              <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => { if (confirm('Delete this item?')) onItemDelete?.(item.id); }}>Delete</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>
    </Paper>
  );
};

export default RecentView;
